// src/components/CustomerFlow/Steps/DocumentUploadStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCamera, 
  faImages, 
  faFileAlt,
  faIdCard,
  faIdCardAlt,
  faFileInvoiceDollar,
  faUserCircle,
  faSmile,
  faIdBadge,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { reportMobileAuthError } from '../../../utils/paymentWidgetUtils';
import Swal from 'sweetalert2';
import { formatCurrency } from '../../../utils/numberFormatter';
import { trackMobileDocumentUploadStepEntered, trackMobileDocumentUploaded } from '../../../analytics/events';
import './DocumentUploadStep.css';

const DOCUMENTS_MAP = {
  documentFront: { name: 'DNI Frente', icon: faIdCard },
  documentBack: { name: 'DNI Dorso', icon: faIdCardAlt },
  bankStatement: { name: 'Extracto de cuenta', icon: faFileInvoiceDollar },
  paycheck: { name: 'Recibo de sueldo', icon: faFileInvoiceDollar },
  selfie: { name: 'Selfie', icon: faUserCircle },
  selfieSmiling: { name: 'Selfie sonriendo', icon: faSmile },
  selfieProduct: { name: 'Selfie con el producto', icon: faUserCircle },
  selfieDocument: { name: 'Selfie con tu documento', icon: faIdBadge },
  service: { name: 'Servicio', icon: faFileInvoiceDollar },
  other: { name: 'Otros', icon: faFileAlt },
};

const DocumentUploadStep = ({ 
  flowData, 
  stepStatuses,
  onNext,
  onSessionExpired,
  searchParams,
  // Props adicionales para uso standalone
  standalone = false,
  tokenScope = null,
  onComplete = null
}) => {
  const [documents, setDocuments] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [purchaseRequest, setPurchaseRequest] = useState(null);
  const [isDocumentsInitialized, setIsDocumentsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isCameraOnlyDocumentUpload = process.env.REACT_APP_MOBILE_DOCUMENT_UPLOAD_CAMERA_ONLY === 'true';

  useEffect(() => {
    initializeStep();
  }, []);

  // Track document upload step entered when purchaseRequest is loaded
  useEffect(() => {
    if (purchaseRequest && flowData?.flow_id) {
      const branchId = localStorage.getItem('temp_tokenBranchId');
      const customerDni = purchaseRequest?.customer?.dni;
      trackMobileDocumentUploadStepEntered({
        commerceId: branchId || 'mobile_flow', // Fallback
        branchId: branchId || undefined,
        customerDni: customerDni,
        flowId: flowData?.flow_id
      });
    }
  }, [purchaseRequest, flowData?.flow_id]);

  useEffect(() => {
    if (isDocumentsInitialized && areRequiredDocsUploaded()) {
      if (standalone && onComplete) {
        // Modo standalone: llamar callback personalizado
        onComplete({
          documents_uploaded: true,
          completion_timestamp: new Date().toISOString(),
          documents: documents
        });
      } else if (onNext) {
        // Modo flujo: notificar al flujo que el paso está completado
        onNext({
          step_name: 'DocumentUploadStep',
          step_status: 'completed',
          documents_uploaded: true,
          completion_timestamp: new Date().toISOString()
        }, 'completed');
      }
    }
  }, [documents, isDocumentsInitialized, standalone, onNext, onComplete]);

  const initializeStep = async () => {
    try {
      setIsLoading(true);
      
      // Obtener información de la solicitud de compra
      await fetchPurchaseRequest();
      
      // Inicializar documentos
      initializeDocuments();
      
    } catch (error) {
      console.error('Error initializing document upload step:', error);
      if (reportMobileAuthError(error, onSessionExpired)) {
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPurchaseRequest = async () => {
    try {
      let customerId, purchaseRequestId;
      
      if (standalone && tokenScope) {
        // Modo standalone: usar tokenScope
        customerId = tokenScope.customer_id;
        purchaseRequestId = tokenScope.purchase_request_id;
      } else if (flowData) {
        // Modo flujo: usar flowData
        customerId = flowData.tokenCustomerId;
        purchaseRequestId = flowData.tokenPurchaseRequestId;
      } else {
        throw new Error('No se encontraron datos de customer o purchase request');
      }
      
      const response = await api.get(
        `/customers/${customerId}/purchase-requests/${purchaseRequestId}`
      );
      setPurchaseRequest(response.data);
    } catch (error) {
      console.error('Error fetching purchase request:', error);
      if (reportMobileAuthError(error, onSessionExpired)) {
        return;
      }
    }
  };

  const initializeDocuments = () => {
    const requiredDocs = process.env.REACT_APP_FLOW_NEW_REQUIRED_DOCS?.split(',') || [];
    const optionalDocs = process.env.REACT_APP_FLOW_NEW_OPTIONAL_DOCS?.split(',') || [];
    const initialDocuments = {};

    requiredDocs.forEach((doc) => {
      initialDocuments[doc] = { 
        status: 'pending', 
        optional: false 
      };
    });

    optionalDocs.forEach((doc) => {
      initialDocuments[doc] = { 
        status: 'pending', 
        optional: true 
      };
    });

    setDocuments(initialDocuments);
    setIsDocumentsInitialized(true);
  };

  const handleFileInput = async (documentType, file) => {
    try {
      setUploadProgress(prev => ({
        ...prev,
        [documentType]: 0
      }));

      const formData = new FormData();
      formData.append('files[]', file);
      formData.append('categories[]', documentType);

      let customerId, purchaseRequestId;
      
      if (standalone && tokenScope) {
        // Modo standalone: usar tokenScope
        customerId = tokenScope.customer_id;
        purchaseRequestId = tokenScope.purchase_request_id;
      } else if (flowData) {
        // Modo flujo: usar flowData
        customerId = flowData.tokenCustomerId;
        purchaseRequestId = flowData.tokenPurchaseRequestId;
      } else {
        throw new Error('No se encontraron datos de customer o purchase request');
      }
      
      const response = await api.post(
        `/customers/${customerId}/purchase-requests/${purchaseRequestId}/documents`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(prev => ({
              ...prev,
              [documentType]: percentCompleted
            }));
          }
        }
      );

      // Limpiamos el progreso
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[documentType];
        return newProgress;
      });

      // Actualizamos el estado del documento
      setDocuments(prev => ({
        ...prev,
        [documentType]: {
          ...prev[documentType],
          status: 'uploaded'
        }
      }));

      // Track document uploaded
      const branchId = localStorage.getItem('temp_tokenBranchId');
      const customerDni = purchaseRequest?.customer?.dni;
      trackMobileDocumentUploaded({
        commerceId: branchId || 'mobile_flow', // Fallback
        branchId: branchId || undefined,
        documentCategory: documentType,
        customerDni: customerDni,
        flowId: flowData?.flow_id
      });

      Swal.fire({
        title: '¡Listo!',
        text: 'Subiste el documento correctamente',
        icon: 'success',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000
      });

    } catch (error) {
      console.error('Error uploading document:', error);
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[documentType];
        return newProgress;
      });

      if (reportMobileAuthError(error, onSessionExpired)) {
        return;
      }
      
      Swal.fire({
        title: 'Error',
        text: 'No pudimos subir el documento. Intentá de nuevo.',
        icon: 'error'
      });
    }
  };

  const areRequiredDocsUploaded = () => {
    return Object.values(documents).every(doc => 
      doc.optional || doc.status === 'uploaded'
    );
  };

  const renderDocumentCard = (docType, docInfo) => {
    const isUploaded = docInfo.status === 'uploaded';
    const isUploading = uploadProgress[docType] !== undefined;
    const progress = uploadProgress[docType] || 0;

    return (
      <div key={docType} className="mob-document-upload-container">
        {isUploaded ? (
          <div className="mob-document-uploaded">
            <FontAwesomeIcon icon={DOCUMENTS_MAP[docType]?.icon || faFileAlt} className="me-2" />
            {DOCUMENTS_MAP[docType]?.name || docType}
            {docInfo.optional && <span className="badge bg-secondary ms-2">Opcional</span>}
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          </div>
        ) : (
          <div className="mob-document-pending">
            <div className="mob-document-title">
              <FontAwesomeIcon icon={DOCUMENTS_MAP[docType]?.icon || faFileAlt} className="me-2" />
              {DOCUMENTS_MAP[docType]?.name || docType}
              {docInfo.optional && <span className="badge bg-secondary ms-2">Opcional</span>}
            </div>
            {isUploading ? (
              <div className="upload-progress">
                <div className="progress">
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: `${progress}%` }}
                    aria-valuenow={progress} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {progress}%
                  </div>
                </div>
                <small className="text-muted mt-1">Subiendo...</small>
              </div>
            ) : (
              <div className="mob-document-actions">
                <input
                  type="file"
                  id={`camera-${docType}`}
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFileInput(docType, e.target.files[0])}
                  style={{ display: 'none' }}
                />
                {!isCameraOnlyDocumentUpload && (
                  <input
                    type="file"
                    id={`gallery-${docType}`}
                    accept="image/*"
                    onChange={(e) => handleFileInput(docType, e.target.files[0])}
                    style={{ display: 'none' }}
                  />
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => document.getElementById(`camera-${docType}`).click()}
                >
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Cámara
                </button>
                {!isCameraOnlyDocumentUpload && (
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => document.getElementById(`gallery-${docType}`).click()}
                  >
                    <FontAwesomeIcon icon={faImages} className="me-2" />
                    Galería
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading || !isDocumentsInitialized) {
    return (
      <div className="document-upload-step bg-white p-4 rounded-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p>Preparando para que subas tus documentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="document-upload-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3>Subí tus documentos</h3>
        <p className="text-muted">
          Subí los documentos que te pedimos para seguir con tu compra
        </p>
      </div>

      {/* Información de la Compra */}
      {purchaseRequest && (
        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title mb-3">Detalles de tu compra</h5>
            
            <div className="row g-3">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted d-block">Cliente</small>
                    <strong>{purchaseRequest.customer.name}</strong>
                  </div>
                  <div className="text-end">
                    <small className="text-muted d-block">DNI</small>
                    <strong>{purchaseRequest.customer.dni}</strong>
                  </div>
                </div>
              </div>
              
              <div className="col-12">
                <hr className="my-2" />
              </div>
              
              <div className="col-6">
                <small className="text-muted d-block">Vendedor</small>
                <strong>{purchaseRequest.seller.full_name}</strong>
              </div>
              
              <div className="col-6 text-end">
                <small className="text-muted d-block">Sucursal</small>
                <strong>{purchaseRequest.branch.name}</strong>
              </div>
              
              <div className="col-12">
                <hr className="my-2" />
              </div>

              <div className="col-6">
                <small className="text-muted d-block">Producto</small>
                <strong>{purchaseRequest.product.name}</strong>
              </div>

              <div className="col-6 text-end">
                <small className="text-muted d-block">Monto</small>
                <strong>{formatCurrency(purchaseRequest.amount, 2)}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Documentos Requeridos */}
      <h6 className="mb-3">
        Documentos obligatorios
        {areRequiredDocsUploaded() && (
          <span className="badge bg-success ms-2">
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            Listo
          </span>
        )}
      </h6>
      <div className="documents-grid mb-4">
        {Object.entries(documents)
          .filter(([_, doc]) => !doc.optional)
          .map(([docType, docInfo]) => renderDocumentCard(docType, docInfo))}
      </div>

      {/* Documentos Opcionales */}
      {Object.entries(documents).some(([_, doc]) => doc.optional) && (
        <>
          <h6 className="mb-3">Documentos opcionales</h6>
          <div className="documents-grid">
            {Object.entries(documents)
              .filter(([_, doc]) => doc.optional)
              .map(([docType, docInfo]) => renderDocumentCard(docType, docInfo))}
          </div>
        </>
      )}

      {/* Mensaje de progreso */}
      {areRequiredDocsUploaded() && (
        <div className="alert alert-success text-center mt-4">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          <strong>¡Ya subiste todos los documentos!</strong>
          <br />
          <small className="text-muted">
            Avísale a <strong>{purchaseRequest?.seller?.full_name}</strong> que terminaste de subir los documentos.
          </small>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadStep;
